import React, { useEffect, useState } from 'react';
import AiTrainerPersonaPicker from './AiTrainerPersonaPicker';
import { commonTimeZoneOptions, detectBrowserTimeZone, formatTrainerSnoozeUntil } from '../../utils/aiTrainerApi';
import {
	fitnessProfileModalBackdropClassName,
	fitnessProfileModalPanelClassName
} from '../FitnessProfile/shared/fitnessProfileReadingStyles';
import {
	aiTrainerHeadingClassName,
	aiTrainerInputClassName,
	aiTrainerMutedTextClassName,
	aiTrainerPrimaryButtonClassName,
	aiTrainerSecondaryButtonClassName
} from './aiTrainerStyles';

export default function AiTrainerSettingsSheet({
	open,
	settings,
	onClose,
	onSave,
	onSnoozeNudges,
	onResumeNudges,
	snoozeLoading = false,
	saving,
	error
}) {
	const [enabled, setEnabled] = useState(settings?.enabled ?? false);
	const [personality, setPersonality] = useState(settings?.personality || 'accountability_partner');
	const [nudgesEnabled, setNudgesEnabled] = useState(settings?.nudges_enabled ?? true);
	const [timeZone, setTimeZone] = useState(settings?.time_zone || detectBrowserTimeZone());

	useEffect(() => {
		if (!open) return;
		setEnabled(settings?.enabled ?? false);
		setPersonality(settings?.personality || 'accountability_partner');
		setNudgesEnabled(settings?.nudges_enabled ?? true);
		setTimeZone(settings?.time_zone || detectBrowserTimeZone());
	}, [open, settings]);

	if (!open) return null;

	const snoozeLabel = formatTrainerSnoozeUntil(settings?.nudge_snoozed_until, settings?.time_zone);

	return (
		<div className={fitnessProfileModalBackdropClassName}>
			<div
				className={`${fitnessProfileModalPanelClassName} max-h-screen overflow-y-auto sm:max-w-lg`}
				style={{ maxHeight: '90vh' }}
			>
				<div className="flex items-center justify-between">
					<h2 className={`text-lg font-bold ${aiTrainerHeadingClassName}`}>Trainer settings</h2>
					<button
						type="button"
						onClick={onClose}
						className="text-gray-500 dark:text-dark-text-secondary hover:text-gray-800 dark:hover:text-dark-text"
						aria-label="Close settings"
					>
						✕
					</button>
				</div>

				<label className={`mt-4 flex items-center gap-2 text-sm ${aiTrainerHeadingClassName}`}>
					<input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
					Enable AI trainer
				</label>

				<div className="mt-4">
					<div className={`mb-2 text-sm font-medium ${aiTrainerHeadingClassName}`}>Personality</div>
					<AiTrainerPersonaPicker value={personality} onChange={setPersonality} />
				</div>

				<label className={`mt-4 block text-sm font-medium ${aiTrainerHeadingClassName}`}>
					Time zone
					<select
						value={timeZone}
						onChange={(e) => setTimeZone(e.target.value)}
						className={`mt-1 ${aiTrainerInputClassName}`}
					>
						{commonTimeZoneOptions().map((zone) => (
							<option key={zone} value={zone}>
								{zone.replace('_', ' ')}
							</option>
						))}
					</select>
				</label>

				<label className={`mt-4 flex items-center gap-2 text-sm ${aiTrainerHeadingClassName}`}>
					<input
						type="checkbox"
						checked={nudgesEnabled}
						onChange={(e) => setNudgesEnabled(e.target.checked)}
					/>
					Proactive nudges
				</label>

				{onSnoozeNudges && (
					<div className="mt-5 rounded-xl border border-gray-200 dark:border-dark-border bg-gray-50 dark:bg-dark-bg px-4 py-4">
						<p className={`text-sm font-medium ${aiTrainerHeadingClassName}`}>Pause reminders</p>
						<p className={`mt-1 text-xs leading-relaxed ${aiTrainerMutedTextClassName}`}>
							Temporarily stop Coach Brain push, email, and briefing nudges. Chat stays available.
						</p>
						{settings?.nudges_snoozed && snoozeLabel && (
							<p className={`mt-3 text-sm ${aiTrainerHeadingClassName}`}>
								Paused until {snoozeLabel}
							</p>
						)}
						<div className="mt-3 flex flex-wrap gap-2">
							<button
								type="button"
								disabled={snoozeLoading}
								onClick={() => onSnoozeNudges(2)}
								className={`${aiTrainerSecondaryButtonClassName} text-xs px-3 py-1.5`}
							>
								Snooze 2h
							</button>
							<button
								type="button"
								disabled={snoozeLoading}
								onClick={() => onSnoozeNudges(null)}
								className={`${aiTrainerSecondaryButtonClassName} text-xs px-3 py-1.5`}
							>
								Until tomorrow
							</button>
							{settings?.nudges_snoozed && onResumeNudges && (
								<button
									type="button"
									disabled={snoozeLoading}
									onClick={onResumeNudges}
									className={`${aiTrainerPrimaryButtonClassName} text-xs px-3 py-1.5`}
								>
									Resume reminders
								</button>
							)}
						</div>
					</div>
				)}

				{error && <p className="mt-3 text-sm text-red-600 dark:text-red-300">{error}</p>}

				<div className="mt-6 flex justify-end gap-2">
					<button type="button" onClick={onClose} className={aiTrainerSecondaryButtonClassName}>
						Cancel
					</button>
					<button
						type="button"
						disabled={saving}
						onClick={() =>
							onSave({
								ai_trainer_enabled: enabled,
								ai_trainer_personality: personality,
								ai_trainer_nudges_enabled: nudgesEnabled,
								time_zone: timeZone
							})
						}
						className={aiTrainerPrimaryButtonClassName}
					>
						{saving ? 'Saving…' : 'Save'}
					</button>
				</div>
			</div>
		</div>
	);
}
